import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Settings, 
  Plus, 
  Edit, 
  Save, 
  X, 
  Trash2,
  DollarSign,
  Users,
  Gamepad2,
  Shield,
  Zap
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';
import toast from 'react-hot-toast';

const SubscriptionPlanManagement = () => {
  const [editingPlan, setEditingPlan] = useState(null);
  const [showAddPlan, setShowAddPlan] = useState(false);
  const queryClient = useQueryClient();

  // Fetch subscription plans
  const { data: plansData, isLoading, error } = useQuery(
    'subscriptionPlans',
    () => adminAPI.getSubscriptionPlans().then(res => {
      // API returns {success: true, data: {plans: [...]}}
      // res.data is the axios response.data which is {success: true, data: {...}}
      // So we need to access res.data.data.plans
      return res.data?.data?.plans || res.data?.plans || [];
    }),
    {
      enabled: true,
      onSuccess: (data) => {
        console.log('Subscription plans loaded:', data);
      },
      onError: (error) => {
        console.error('Subscription plans error:', error);
      }
    }
  );

  // Update plan mutation
  const updatePlanMutation = useMutation(
    ({ planId, planData }) => adminAPI.updateSubscriptionPlan(planId, planData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('subscriptionPlans');
        queryClient.invalidateQueries('serverPlans'); // Also invalidate server plans
        setEditingPlan(null);
        toast.success('Plan updated successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update plan');
      }
    }
  );

  // Add plan mutation
  const addPlanMutation = useMutation(
    (planData) => adminAPI.addSubscriptionPlan(planData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('subscriptionPlans');
        queryClient.invalidateQueries('serverPlans'); // Also invalidate server plans
        setShowAddPlan(false);
        toast.success('Plan added successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to add plan');
      }
    }
  );

  // Delete plan mutation
  const deletePlanMutation = useMutation(
    (planId) => adminAPI.deleteSubscriptionPlan(planId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('subscriptionPlans');
        queryClient.invalidateQueries('serverPlans'); // Also invalidate server plans
        toast.success('Plan deleted successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete plan');
      }
    }
  );

  // Seed plans mutation
  const seedPlansMutation = useMutation(
    () => adminAPI.seedSubscriptionPlans(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('subscriptionPlans');
        queryClient.invalidateQueries('serverPlans'); // Also invalidate server plans
        toast.success('Default plans seeded successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to seed plans');
      }
    }
  );

  const handleEditPlan = (plan) => {
    setEditingPlan({ ...plan });
  };

  const handleSavePlan = () => {
    updatePlanMutation.mutate({
      planId: editingPlan.id,
      planData: editingPlan
    });
  };

  const handleAddPlan = (newPlan) => {
    addPlanMutation.mutate(newPlan);
  };

  const handleDeletePlan = (planId) => {
    if (planId === 'free') {
      toast.error('Cannot delete the free plan - it is required for the system');
      return;
    }
    if (window.confirm('Are you sure you want to delete this plan?')) {
      deletePlanMutation.mutate(planId);
    }
  };

  const handleSeedPlans = () => {
    if (window.confirm('This will seed the default Free and Pro plans into the database. Continue?')) {
      seedPlansMutation.mutate();
    }
  };

  const formatPrice = (price) => {
    return price === 0 ? 'Free' : `$${(price / 100).toFixed(2)}`;
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'free': return <Shield className="h-6 w-6 text-gray-400" />;
      case 'premium': return <Zap className="h-6 w-6 text-blue-500" />;
      case 'pro': return <Gamepad2 className="h-6 w-6 text-purple-500" />;
      case 'enterprise': return <Users className="h-6 w-6 text-green-500" />;
      default: return <Settings className="h-6 w-6 text-gray-500" />;
    }
  };

  if (isLoading) {
    return <LoadingSpinner size="xl" />;
  }

  if (error) {
    return (
      <div className="text-red-400">
        Error loading subscription plans: {error.message}
      </div>
    );
  }

  // plansData is already the plans array from the API call transformation above
  const plans = plansData || [];
  
  console.log('Full API response:', plansData);
  console.log('Plans array:', plans);
  console.log('Plans length:', plans.length);

  if (plans.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-discord-lightest">Subscription Plans</h2>
            <p className="text-discord-light">Manage server subscription plans and pricing</p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowAddPlan(true)}
              className="btn btn-primary flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Plan</span>
            </button>
            {/* <button
              onClick={handleSeedPlans}
              className="btn btn-secondary flex items-center space-x-2"
            >
              <Settings className="h-4 w-4" />
              <span>Seed Default Plans</span>
            </button> */}
          </div>
        </div>
        
        {/* Debug information */}
        <div className="bg-discord-darker p-4 rounded-lg border border-discord-dark">
          <h3 className="text-lg font-semibold text-discord-lightest mb-2">Debug Information</h3>
          <div className="text-sm text-discord-light space-y-1">
            <p><strong>Loading:</strong> {isLoading ? 'Yes' : 'No'}</p>
            <p><strong>Error:</strong> {error ? error.message : 'None'}</p>
            <p><strong>Plans Data:</strong> {plansData ? 'Received' : 'Not received'}</p>
            <p><strong>Plans Array:</strong> {plans.length} items</p>
            <p><strong>Is Array:</strong> {Array.isArray(plansData) ? 'Yes' : 'No'}</p>
            {plansData && !Array.isArray(plansData) && (
              <div className="mt-2">
                <p><strong>Data Type:</strong> {typeof plansData}</p>
                <p><strong>Data Keys:</strong> {Object.keys(plansData).join(', ')}</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="text-center py-12">
          <Settings className="h-12 w-12 text-discord-light mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-discord-lightest mb-2">No Subscription Plans</h3>
          <p className="text-discord-light mb-6">Get started by seeding default plans or creating a custom plan.</p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={handleSeedPlans}
              className="btn btn-primary"
            >
              Seed Default Plans
            </button>
            <button
              onClick={() => setShowAddPlan(true)}
              className="btn btn-secondary"
            >
              Create Custom Plan
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-discord-lightest">Subscription Plans</h2>
          <p className="text-discord-light">Manage server subscription plans and pricing</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowAddPlan(true)}
            className="btn btn-primary flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Add Plan</span>
          </button>
          {/* <button
            onClick={handleSeedPlans}
            className="btn btn-secondary flex items-center space-x-2"
          >
            <Settings className="h-4 w-4" />
            <span>Seed Default Plans</span>
          </button> */}
        </div>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan) => (
          <div key={plan.id} className="card relative">
            {/* Plan Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                {getPlanIcon(plan.id)}
                <div>
                  <h3 className="text-lg font-semibold text-discord-lightest">{plan.name}</h3>
                  <p className="text-sm text-discord-light">{plan.description}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-discord-blurple">
                  {formatPrice(plan.price)}
                </div>
                <div className="text-sm text-discord-light">
                  {plan.interval === 'forever' ? 'Forever' : `/ ${plan.interval}`}
                </div>
              </div>
            </div>

            {/* Available Games */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-discord-lightest mb-2">Available Games</h4>
              <div className="flex flex-wrap gap-1">
                {plan.availableGames.map((game) => (
                  <span
                    key={game}
                    className="px-2 py-1 bg-discord-dark text-discord-light text-xs rounded"
                  >
                    {game.replace('-', ' ')}
                  </span>
                ))}
              </div>
            </div>

            {/* Key Features */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-discord-lightest mb-2">Key Features</h4>
              <ul className="space-y-1">
                {plan.features.customThemes && (
                  <li className="text-xs text-discord-light">✓ Custom Themes</li>
                )}
                {plan.features.analytics && (
                  <li className="text-xs text-discord-light">✓ Analytics</li>
                )}
                {plan.features.prioritySupport && (
                  <li className="text-xs text-discord-light">✓ Priority Support</li>
                )}
                {plan.features.apiAccess && (
                  <li className="text-xs text-discord-light">✓ API Access</li>
                )}
                {plan.features.whiteLabel && (
                  <li className="text-xs text-discord-light">✓ White Label</li>
                )}
              </ul>
            </div>

            {/* Limits */}
            <div className="mb-4">
              <h4 className="text-sm font-medium text-discord-lightest mb-2">Limits</h4>
              <div className="space-y-1">
                <div className="text-xs text-discord-light">
                  Games/Day: {plan.limits.gamesPerDay}
                </div>
                <div className="text-xs text-discord-light">
                  Games/Month: {plan.limits.gamesPerMonth}
                </div>
                <div className="text-xs text-discord-light">
                  Max Players: {plan.limits.maxPlayersPerGame}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-discord-dark">
              <button
                onClick={() => handleEditPlan(plan)}
                className="btn btn-secondary btn-sm flex items-center space-x-1"
              >
                <Edit className="h-3 w-3" />
                <span>Edit</span>
              </button>
              {plan.id !== 'free' && (
                <button
                  onClick={() => handleDeletePlan(plan.id)}
                  className="btn btn-danger btn-sm flex items-center space-x-1"
                >
                  <Trash2 className="h-3 w-3" />
                  <span>Delete</span>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Edit Plan Modal */}
      {editingPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-discord-dark rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-discord-lightest">Edit Plan: {editingPlan.name}</h3>
              <button
                onClick={() => setEditingPlan(null)}
                className="text-discord-light hover:text-discord-lightest"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-discord-light mb-2">Name</label>
                  <input
                    type="text"
                    value={editingPlan.name}
                    onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-discord-light mb-2">Price (cents)</label>
                  <input
                    type="number"
                    value={editingPlan.price}
                    onChange={(e) => setEditingPlan({ ...editingPlan, price: parseInt(e.target.value) })}
                    className="input w-full"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-discord-light mb-2">Description</label>
                <textarea
                  value={editingPlan.description}
                  onChange={(e) => setEditingPlan({ ...editingPlan, description: e.target.value })}
                  className="input w-full h-20"
                />
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-discord-light mb-2">Features</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(editingPlan.features).map(([key, value]) => (
                    <label key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setEditingPlan({
                          ...editingPlan,
                          features: { ...editingPlan.features, [key]: e.target.checked }
                        })}
                        className="rounded"
                      />
                      <span className="text-sm text-discord-light capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Limits */}
              <div>
                <label className="block text-sm font-medium text-discord-light mb-2">Limits</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-discord-light mb-1">Games Per Day</label>
                    <input
                      type="text"
                      value={editingPlan.limits.gamesPerDay}
                      onChange={(e) => setEditingPlan({
                        ...editingPlan,
                        limits: { ...editingPlan.limits, gamesPerDay: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-discord-light mb-1">Games Per Month</label>
                    <input
                      type="text"
                      value={editingPlan.limits.gamesPerMonth}
                      onChange={(e) => setEditingPlan({
                        ...editingPlan,
                        limits: { ...editingPlan.limits, gamesPerMonth: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-discord-light mb-1">Max Players</label>
                    <input
                      type="number"
                      value={editingPlan.limits.maxPlayersPerGame}
                      onChange={(e) => setEditingPlan({
                        ...editingPlan,
                        limits: { ...editingPlan.limits, maxPlayersPerGame: parseInt(e.target.value) }
                      })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-discord-light mb-1">Server Members</label>
                    <input
                      type="text"
                      value={editingPlan.limits.serverMembers}
                      onChange={(e) => setEditingPlan({
                        ...editingPlan,
                        limits: { ...editingPlan.limits, serverMembers: e.target.value }
                      })}
                      className="input w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Available Games */}
              <div>
                <label className="block text-sm font-medium text-discord-light mb-2">Available Games</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    'tic-tac-toe', 'number-guess', 'rock-paper-scissors',
                    'hangman', 'word-guess', 'trivia', 'chess', 'checkers',
                    'connect-four', 'battleship', 'battle-grid', 'custom-game'
                  ].map((game) => (
                    <label key={game} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={editingPlan.availableGames.includes(game)}
                        onChange={(e) => {
                          const games = e.target.checked
                            ? [...editingPlan.availableGames, game]
                            : editingPlan.availableGames.filter(g => g !== game);
                          setEditingPlan({ ...editingPlan, availableGames: games });
                        }}
                        className="rounded"
                      />
                      <span className="text-sm text-discord-light">
                        {game.replace('-', ' ')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-discord-dark">
                <button
                  onClick={() => setEditingPlan(null)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePlan}
                  disabled={updatePlanMutation.isLoading}
                  className="btn btn-primary flex items-center space-x-2"
                >
                  <Save className="h-4 w-4" />
                  <span>Save Changes</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Plan Modal */}
      {showAddPlan && (
        <AddPlanModal
          onClose={() => setShowAddPlan(false)}
          onAdd={handleAddPlan}
          isLoading={addPlanMutation.isLoading}
        />
      )}
    </div>
  );
};

// Add Plan Modal Component
const AddPlanModal = ({ onClose, onAdd, isLoading }) => {
  const [newPlan, setNewPlan] = useState({
    id: '',
    name: '',
    price: 0,
    interval: 'month',
    description: '',
    features: {
      basicGames: true,
      customThemes: false,
      analytics: false,
      prioritySupport: false,
      customCommands: false,
      advancedSettings: false,
      webhooks: false,
      apiAccess: false,
      whiteLabel: false,
      customBranding: false
    },
    limits: {
      gamesPerDay: 50,
      gamesPerMonth: 1500,
      maxPlayersPerGame: 10,
      serverMembers: 1000,
      customCommands: 5,
      webhookCalls: 500
    },
    availableGames: ['tic-tac-toe', 'number-guess'],
    restrictions: {
      gameHistoryRetention: '30 days',
      leaderboardRetention: '6 months',
      supportResponse: '24-48 hours'
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAdd(newPlan);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-discord-dark rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-discord-lightest">Add New Plan</h3>
          <button
            onClick={onClose}
            className="text-discord-light hover:text-discord-lightest"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-discord-light mb-2">Plan ID</label>
              <input
                type="text"
                value={newPlan.id}
                onChange={(e) => setNewPlan({ ...newPlan, id: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-discord-light mb-2">Name</label>
              <input
                type="text"
                value={newPlan.name}
                onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                className="input w-full"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-discord-light mb-2">Price (cents)</label>
              <input
                type="number"
                value={newPlan.price}
                onChange={(e) => setNewPlan({ ...newPlan, price: parseInt(e.target.value) })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-discord-light mb-2">Interval</label>
              <select
                value={newPlan.interval}
                onChange={(e) => setNewPlan({ ...newPlan, interval: e.target.value })}
                className="input w-full"
              >
                <option value="month">Month</option>
                <option value="year">Year</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-discord-light mb-2">Description</label>
            <textarea
              value={newPlan.description}
              onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
              className="input w-full h-20"
              required
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-discord-dark">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Add Plan</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubscriptionPlanManagement;
